import * as React from 'react';
import { ThemeProvider } from 'styled-components';
import { Snackbar, createTheme, AppBar, Toolbar, Theme, Typography, IconButton, Box, Card, CardActionArea, CardContent, Stack } from '@mui/material';
import { responsiveFontSizes } from '@mui/material/styles';
import { Link as RouterLink, useParams } from 'react-router-dom';

import MenuIcon from '@mui/icons-material/Menu';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import { APIProvider } from '@vis.gl/react-google-maps';

import { useAppStateStore, useEventInfoStore, useUIStateStore, useVotingPlaceStore, SearchType, useDirectionsStore } from '../../store/layerStore';
import { fetchEventInfo } from "../../api";
import LogoImage from '../LogoImage';
import MenuDrawer from '../MenuDrawer';
import { IEventInfo } from '../../model/Event';
import { AppScopes, AppStates, UserStates } from '../../util';
import Content from './Content';
import AzureAutocomplete from '../Search/AzureAutocomplete';
import Loading from './Loading';
import '@bcgov/bc-sans/css/BC_Sans.css';
// import { fetchEventInfoPromise } from '../../api/startup';

const API_KEY = 'AIzaSyAjxt6nhZhlXOZvKqoFRVDbVzxa_GL_Jg4';

const drawerWidth = '485px'; //'485px';
const baseHeaderHeight = 64; // <Toolbar /> default height
const baseSearchHeight = 64; // <Toolbar /> default height
const baseFooterHeight = 0;//56;

enum DrawerContentType {
    Menu = 1,
    Help,
}

type LandingEvent = {
    eventKey: string,
    eventInfo: IEventInfo,
}

type LandingTarget = {
    eventKey: string,
    configKey: string,
}

const themedStyles = (theme: Theme) => {
    return {
        appBar: {
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: "#db282e",
        },
        infoArea: {
            backgroundColor: "#e9e8dc",
            fontFamily: 'Bebas Neue',
        },    
    }
}

const getDefaultConfigKey = () => {
    // 1) Build-time override (works when each slot has its own build artifact).
    const envValue = toUpperTrimmed(process.env.REACT_APP_DEFAULT_CONFIG_KEY);
    if (envValue === 'PRODUCTION' || envValue === 'STAGING') {
        return envValue;
    }

    // 2) Runtime override (works when the same artifact is deployed to multiple slots).
    const runtimeValue = toUpperTrimmed((window as any).__APP_CONFIG__?.defaultConfigKey);
    if (runtimeValue === 'PRODUCTION' || runtimeValue === 'STAGING') {
        return runtimeValue;
    }

    // 3) Slot hostname fallback for Azure App Service (for example: "*-staging.azurewebsites.net").
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes('staging') || hostname.includes('localhost')) {
        return 'STAGING';
    }

    return 'PRODUCTION';
};

const toUpperTrimmed = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed.toUpperCase() : undefined;
};

const parseEventKeyFromUrl = (url: unknown): string | undefined => {
    if (typeof url !== 'string') return undefined;
    const segment = url.split('?')[0].split('/').filter(Boolean).pop();
    return toUpperTrimmed(segment);
};

const isMultiScopeEvent = (info: IEventInfo): boolean => {
    const normalizedScope = (info.scope || '').toLowerCase();
    const normalizedState = (info.state || '').toLowerCase();
    return normalizedScope === AppScopes.MULTI || normalizedScope === AppStates.MULTI || normalizedState === AppStates.MULTI;
};

const buildLandingTargets = (info: IEventInfo, baseConfigKey: string): LandingTarget[] => {
    const targets = new Map<string, LandingTarget>();
    const addTarget = (configKey: string | undefined, eventKey: string | undefined) => {
        if (!configKey || !eventKey) return;
        targets.set(configKey, { configKey, eventKey });
    };

    const withBase = (eventKey: string | undefined) => eventKey ? `${baseConfigKey}_${eventKey}` : undefined;

    const landingLinks = (info as IEventInfo & { landingLinks?: unknown[] }).landingLinks;
    if (Array.isArray(landingLinks)) {
        landingLinks.forEach((link) => {
            if (typeof link === 'string') {
                const eventKey = parseEventKeyFromUrl(link) || toUpperTrimmed(link);
                addTarget(withBase(eventKey), eventKey);
                return;
            }

            if (link && typeof link === 'object') {
                const obj = link as Record<string, unknown>;
                const explicitConfigKey = typeof obj.configKey === 'string' ? obj.configKey : undefined;
                const derivedEventKey =
                    toUpperTrimmed(obj.eventKey) ||
                    toUpperTrimmed(obj.key) ||
                    parseEventKeyFromUrl(obj.url) ||
                    (explicitConfigKey ? toUpperTrimmed(explicitConfigKey.split('_').pop()) : undefined);

                addTarget(explicitConfigKey || withBase(derivedEventKey), derivedEventKey);
            }
        });
    }

    if (Array.isArray(info.menuOptions)) {
        info.menuOptions.forEach((option) => {
            const eventKey = parseEventKeyFromUrl(option.url) || toUpperTrimmed(option.key);
            addTarget(withBase(eventKey), eventKey);
        });
    }

    if (Array.isArray(info.electoralDistricts)) {
        info.electoralDistricts.forEach((districtKey) => {
            const eventKey = toUpperTrimmed(districtKey);
            addTarget(withBase(eventKey), eventKey);
        });
    }

    return Array.from(targets.values());
};

const normalizeEventInfo = (info: IEventInfo): IEventInfo => {
    info.advanceVotingDates = info.advanceVotingDates.map(d => new Date(d));
    info.deoOpenDate = new Date(info.deoOpenDate);
    info.holidays = info.holidays.map(d => new Date(d));
    info.holidayInfos = info.holidayInfos.map(h => ({ date: new Date(h.date), name: h.name }));
    info.finalVotingDate = new Date(info.finalVotingDate);
    info.finalVotingDateLocal = new Date(info.finalVotingDateLocal);
    switch (info.state) {
        case AppStates.PRE_ADVANCE:
        case AppStates.ADVANCE_1: info.score = 1; break;
        case AppStates.ADVANCE_2: info.score = 2; break;
        case AppStates.ADVANCE_3: info.score = 4; break;
        case AppStates.ADVANCE_4: info.score = 8; break;
        case AppStates.ADVANCE_5: info.score = 16; break;
        case AppStates.ADVANCE_6: info.score = 32; break;
        case AppStates.ADVANCE_7: info.score = 64; break;
        case AppStates.ADVANCE_8: info.score = 128; break;
        case AppStates.ADVANCE_9: info.score = 256; break;
        default: info.score = 512; break;
    }
    return info;
}

function MultiEventLanding(props: { events: LandingEvent[] }) {
    const dvh = window.CSS.supports('height', '100vh');

    return (
        <Box minHeight={`calc(100${dvh ? 'dvh' : 'vh'} - 128px)`} sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f8f8f5' }}>
            <Typography variant="h4" sx={{ mb: 1, fontFamily: 'BC Sans' }}>Current By-elections</Typography>
            <Typography variant="body1" sx={{ mb: 3, fontFamily: 'BC Sans' }}>
                Select an Electoral District to continue.
            </Typography>

            <Stack spacing={2}>
                {props.events.map((event) => (
                    <Card key={event.eventKey} sx={{ borderLeft: '8px solid #db282e' }}>
                        <CardActionArea component={RouterLink} to={`/${event.eventKey}`}>
                            <CardContent>
                                <Typography variant="h5" sx={{ fontFamily: 'BC Sans', color: '#1f1f1f' }}>
                                    {event.eventInfo.electoralDistrictText}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, color: '#565656', fontFamily: 'BC Sans' }}>
                                    Open this by-election map
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Stack>
        </Box>
    );
}

export default function Layout() {
    const { eventKey } = useParams();
    const normalizedEventKey = eventKey ? eventKey.toUpperCase() : undefined;
    const { setUserLocation, setSearchLocation, message, setMessage, setLoadingPlaceId, setClearSearch } = useAppStateStore();
    const { searchPlace, userState, setLastNearestSearch } = useAppStateStore();
    const { setVotingPlaceSearchResults, setNearestVotingPlaces } = useVotingPlaceStore();
    const {eventInfo, setEventInfo} = useEventInfoStore();
    const { menuDrawerOpen, setMenuDrawerOpen, setMenuDrawerType } = useUIStateStore();
    const [largeScreen, setLargeScreen] = React.useState(false);
    const [isLandingMode, setIsLandingMode] = React.useState(false);
    const [landingEvents, setLandingEvents] = React.useState<LandingEvent[]>([]);

    const [drawerContentType] = React.useState(DrawerContentType.Menu);
    const theme = responsiveFontSizes(createTheme());

    React.useEffect(() => {
        function handleResize() {
            setLargeScreen(window.innerWidth > theme.breakpoints.values.sm);
        }    
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, [theme.breakpoints.values.sm]);

    const fetchEventInfoByConfigKey = React.useCallback((configKey: string): Promise<IEventInfo | undefined> => {
        return new Promise((resolve) => {
            fetchEventInfo({ input: '', configKey }, (info?: IEventInfo) => {
                resolve(info ? normalizeEventInfo(info) : undefined);
            });
        });
    }, []);

    React.useEffect(() => {
        let cancelled = false;

        const initializeLocationState = () => {
            const queryParameters = new URLSearchParams(window.location.search);
            if (queryParameters && queryParameters.has('placeid')) {
                const pid: string | null = queryParameters.get('placeid');
                if (pid) {
                    setLoadingPlaceId(pid);
                }
                return;
            }

            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(function(position) {
                    const pos: google.maps.LatLngLiteral = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };

                    setUserLocation(pos);
                    setSearchLocation(pos);
                    setLastNearestSearch({
                        type: SearchType.LOCATION,
                        location: pos,
                        parameter: null,
                        placeId: null
                    });
                }, () => {
                    setMessage('Geolocation is not supported by this browser. Please enter your location in the search bar, or upgrade your browser');
                });
            } else {
                setMessage('Geolocation is not supported by this browser. Please enter your location in the search bar, or upgrade your browser');
            }
        };

        const load = async () => {
            const baseConfigKey = getDefaultConfigKey();
            const selectedConfigKey = normalizedEventKey ? `${baseConfigKey}_${normalizedEventKey}` : baseConfigKey;
            const info = await fetchEventInfoByConfigKey(selectedConfigKey);

            if (!info || cancelled) {
                return;
            }

            setEventInfo(info);

            const landingTargets = buildLandingTargets(info, baseConfigKey);

            const shouldShowLanding =
                !normalizedEventKey &&
                isMultiScopeEvent(info) &&
                landingTargets.length > 1;

            if (shouldShowLanding) {
                const events = await Promise.all(
                    landingTargets.map(async (target) => {
                        const districtInfo = await fetchEventInfoByConfigKey(target.configKey);
                        if (!districtInfo) return null;

                        return {
                            eventKey: target.eventKey,
                            eventInfo: districtInfo,
                        } as LandingEvent;
                    })
                );

                if (!cancelled) {
                    setLandingEvents(events.filter((e): e is LandingEvent => e !== null));
                    setIsLandingMode(true);
                }
                return;
            }

            if (!cancelled) {
                setIsLandingMode(false);
                setLandingEvents([]);
                if (info.state !== AppStates.PRE_WRIT) {
                    initializeLocationState();
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [fetchEventInfoByConfigKey, normalizedEventKey, setEventInfo, setLastNearestSearch, setLoadingPlaceId, setMessage, setSearchLocation, setUserLocation]);

    const handleMenuDrawerOpen = () => {
        setMenuDrawerType(DrawerContentType.Menu);
        setMenuDrawerOpen(!menuDrawerOpen);
    };

    const handleMyLocationClick = () => {
        // (e) => { setUserLocation(getUserLocation()); }    
        if (navigator.geolocation) {
            setClearSearch(true);
            setVotingPlaceSearchResults([]);
            console.log('**** > setNearestVotingPlaces - F');
            setNearestVotingPlaces(null);            
            navigator.geolocation.getCurrentPosition(function(position) {
                // Create a position object
                const pos: google.maps.LatLngLiteral = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
      
                //panToPoint(pos.lat, pos.lng);
                //getVPsNearUserGeolocation(pos, appState);
                setUserLocation(pos);
                setSearchLocation(pos);
                setLastNearestSearch({
                    type: SearchType.LOCATION, location: pos,
                    parameter: null,
                    placeId: null
                });
                // setMessage(`Location: ${pos.lng}, ${pos.lat}`)
                // return(pos);
            }, () => { setMessage('Geolocation is not supported by this browser. Please enter your location in the search bar, or upgrade your browser'); } );
        } else {
            // Browser doesn't support Geolocation
            //window.Materialize.toast('Geolocation is not supported by this browser. Please enter your location in the search bar, or upgrade your browser', 4000);
            setMessage('Geolocation is not supported by this browser. Please enter your location in the search bar, or upgrade your browser');
            // console.log('Browser does not support geolocation');
        }
        return;
    }

    // return (
    //     <Box sx={{ display: 'flex' }}>
    //         <CircularProgress />
    //     </Box>
    // );
    return (
        // <ThemeProvider theme={ AppBarTheme }>
        <ThemeProvider theme={ theme }>
        <div >
        <Snackbar 
            anchorOrigin={{vertical: 'bottom',horizontal: 'center'}}
            onClose={(event: React.SyntheticEvent | Event, reason?: string) => {
                if (reason === 'clickaway') {
                    return;
                }
                setMessage();
            }}
            message={message}
            open={message !== undefined}
            autoHideDuration={5000}
        />
        <APIProvider apiKey={API_KEY} libraries={['maps','places','routes','marker']} version='3.51' language='en' region='CA' onLoad={() => {}}>
            <AppBar position="fixed" sx={{ ...themedStyles(theme).appBar, zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                {/* <EventInfoListener /> */}
                <Toolbar sx={{ ...themedStyles(theme).infoArea }}>
                    <LogoImage />
                    <Typography variant="h4" style={{ marginLeft: 'auto', fontFamily: 'Bebas Neue', color: 'black' }}>Where to Vote</Typography>
                    {/* <IconButton size="medium" edge="end" aria-label="help" sx={{ color: 'black' }} onClick={() => {}}>
                        <HelpOutline />
                    </IconButton> */}
                </Toolbar>
        
                <Toolbar>
                    { !isLandingMode && eventInfo && (eventInfo.state !== AppStates.PRE_WRIT && eventInfo.state !== AppStates.CLOSED && eventInfo.state !== AppStates.ERROR) &&
                    <>
                        <IconButton size="large" edge="start" color="inherit" aria-label="menu" sx={{ mr: 2 }} onClick={handleMenuDrawerOpen}>
                            <MenuIcon />
                        </IconButton>
                        <AzureAutocomplete defaultPlace={searchPlace} />
                        { userState === UserStates.NB && eventInfo.scope !== AppScopes.BYELECTION &&
                        <IconButton size="large" edge="end" color="inherit" aria-label="menu" sx={{ ml: 0 }} onClick={handleMyLocationClick}>
                            <MyLocationIcon />
                        </IconButton>   
                        }
                    </>
                    }
                </Toolbar>
            </AppBar>
            {!isLandingMode &&
            <MenuDrawer 
                width={drawerWidth} 
                anchor={ drawerContentType === DrawerContentType.Menu ? "left" : "right" }
            />
            }
            <Toolbar id='spacer1' />
            <Toolbar id='spacer2' />
            {isLandingMode
                ? (landingEvents.length > 0
                    ? <MultiEventLanding events={landingEvents} />
                    : <Loading />)
                : <Content drawerWidth={drawerWidth} containerWidth={window.innerWidth} containerHeight={`${baseHeaderHeight + baseSearchHeight + baseFooterHeight - (largeScreen ? 0 : 16)}`} isLargeScreen={largeScreen} />}
        </APIProvider>
        </div>
      </ThemeProvider>
      );

}